import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Briefcase,
  MapPin,
  Building2,
  Clock,
  ExternalLink,
  Filter,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobMatchesPage = () => {
  const { token } = useAuth();
  const [matches, setMatches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState('all');

  const fetchData = async () => {
    try {
      const [matchesRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs/matches`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setMatches(matchesRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load job matches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const markAsRead = async (matchId) => {
    try {
      await axios.put(
        `${API_URL}/api/jobs/matches/${matchId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches(matches.map((m) => (m.id === matchId ? { ...m, is_read: true } : m)));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API_URL}/api/jobs/matches/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches(matches.map((m) => ({ ...m, is_read: true })));
      toast.success('All job matches marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const clearAllMatches = async () => {
    if (!window.confirm('Are you sure you want to clear all job matches? This cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/api/jobs/matches/clear-all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches([]);
      toast.success('All job matches cleared');
    } catch (error) {
      toast.error('Failed to clear job matches');
    }
  };

  const filteredMatches = selectedAlert === 'all'
    ? matches
    : matches.filter((m) => m.alert_id === selectedAlert);

  const unreadCount = matches.filter((m) => !m.is_read).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="job-matches-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Job Matches</h1>
            <p className="text-slate-600 mt-1">
              {unreadCount > 0 ? `${unreadCount} new match${unreadCount !== 1 ? 'es' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select value={selectedAlert} onValueChange={setSelectedAlert}>
                <SelectTrigger className="w-[180px]" data-testid="filter-alert-select">
                  <SelectValue placeholder="Filter by alert" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All alerts</SelectItem>
                  {alerts.map((alert) => (
                    <SelectItem key={alert.id} value={alert.id}>
                      {alert.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllAsRead}
                className="text-[#00A651] border-[#00A651] hover:bg-[#E2F5EA]"
                data-testid="mark-all-read-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
            {matches.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllMatches}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="clear-all-matches-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Job Matches List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="h-6 w-64 bg-slate-200 rounded mb-4" />
                <div className="h-4 w-full bg-slate-100 rounded mb-2" />
                <div className="h-4 w-3/4 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : filteredMatches.length > 0 ? (
          <div className="space-y-4">
            {filteredMatches.map((job) => {
              const alertName = alerts.find((a) => a.id === job.alert_id)?.name;
              return (
                <div
                  key={job.id}
                  className={`bg-white rounded-xl border p-6 card-hover ${
                    !job.is_read ? 'border-[#00A651]/50 bg-[#F0FDF4]' : 'border-slate-200'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          !job.is_read ? 'bg-[#00A651]' : 'bg-slate-100'
                        }`}>
                          <Briefcase className={`w-5 h-5 ${!job.is_read ? 'text-white' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{job.title}</h3>
                          {alertName && (
                            <span className="text-xs text-slate-500">
                              Matched from: {alertName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span>{job.employer}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{job.grade}</span>
                        </div>
                        {job.salary && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#00A651]">{job.salary}</span>
                          </div>
                        )}
                      </div>

                      {job.closing_date && (
                        <p className="text-sm text-slate-500 mt-3">
                          Closing: {new Date(job.closing_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-3 lg:flex-col">
                      <a
                        href={job.job_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => !job.is_read && markAsRead(job.id)}
                        className="w-full"
                      >
                        <Button className="w-full bg-[#00A651] hover:bg-[#008C44] text-white" data-testid={`view-job-${job.id}`}>
                          View Job
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                      {!job.is_read && (
                        <button
                          onClick={() => markAsRead(job.id)}
                          className="text-sm text-slate-500 hover:text-[#00A651] flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <Briefcase className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No job matches yet</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              {alerts.length > 0
                ? 'No jobs matching your criteria have been found yet. We\'ll notify you when new matches are available.'
                : 'Create a job alert to start receiving matches for healthcare positions.'}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default JobMatchesPage;
